import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Table,
  Tag,
  Modal,
  message,
  Spin,
  Empty,
  Statistic,
  Progress,
  Alert,
} from 'antd';
import { SwapOutlined, HistoryOutlined, StarFilled, TrophyOutlined } from '@ant-design/icons';
import { loyaltyService } from '../services/api';
import { extractList, extractObject } from '../utils/formatters';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;

const formatNPR = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 2,
  }).format(amount || 0);

const CustomerRewards = () => {
  const [loading, setLoading] = useState(true);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [history, setHistory] = useState([]);
  const [redemptionOptions, setRedemptionOptions] = useState(null);
  const [redeemModal, setRedeemModal] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState(0);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pointsRes, historyRes, optionsRes] = await Promise.all([
        loyaltyService.getLoyaltyPoints().catch(() => ({ data: {} })),
        loyaltyService.getLoyaltyHistory().catch(() => ({ data: {} })),
        loyaltyService.getRedemptionOptions().catch(() => ({ data: {} })),
      ]);

      setLoyaltyData(extractObject(pointsRes.data) || {});

      // The history endpoint nests the page of transactions under
      // `data.transactions`, so a plain `data` array check always came back
      // empty and the history table never rendered.
      const historyBody = extractObject(historyRes.data);
      setHistory(extractList(historyBody?.transactions));

      setRedemptionOptions(extractObject(optionsRes.data) || {});
    } catch (err) {
      console.error('Failed to fetch rewards data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (selectedPoints <= 0) {
      message.warning('Select points to redeem');
      return;
    }
    setRedeeming(true);
    try {
      await loyaltyService.redeemPoints(selectedPoints, 'wallet');
      message.success(`Redeemed ${selectedPoints} points!`);
      setRedeemModal(false);
      setSelectedPoints(0);
      fetchData();
    } catch (err) {
      message.error(
        err.response?.data?.error || err.response?.data?.message || 'Redemption failed',
      );
    } finally {
      setRedeeming(false);
    }
  };

  if (loading)
    return (
      <div className="customer-page" style={{ padding: 0, textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );

  const balance = loyaltyData?.current_balance || loyaltyData?.balance || 0;
  const lifetimePoints = loyaltyData?.lifetime_points || loyaltyData?.total_earned || 0;
  const rate = redemptionOptions?.redemption_rate_value || 1;
  const minRedemption = redemptionOptions?.minimum_redemption || 0;
  const maxRedemption = redemptionOptions?.max_redemption_per_order || balance;
  const presets = redemptionOptions?.redemption_presets || [100, 200, 500, 1000];

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      render: (d) => (d ? new Date(d).toLocaleDateString() : '-'),
      width: 110,
    },
    // The ledger stores `earn` / `redeem`, not `earned` / `redeemed`.
    {
      title: 'Type',
      dataIndex: 'type',
      render: (t) => (
        <Tag color={t === 'earn' ? 'green' : t === 'redeem' ? 'orange' : 'default'}>
          {(t || '').toUpperCase()}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Points',
      dataIndex: 'points',
      render: (p, r) => (
        <Text strong style={{ color: r.type === 'earn' ? '#52b788' : '#e5484d' }}>
          {r.type === 'earn' ? '+' : '-'}
          {Math.abs(p || 0)}
        </Text>
      ),
      width: 80,
    },
    { title: 'Description', dataIndex: 'description', render: (d) => d || '-' },
  ];

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>Loyalty Rewards</Title>
        <Paragraph>
          Earn points on every order and redeem them for wallet credit or discounts.
        </Paragraph>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card className="customer-card">
            <Statistic
              title="Available Points"
              value={balance}
              prefix={<StarFilled style={{ color: '#f1a134' }} />}
              valueStyle={{ color: '#f1a134', fontSize: 32 }}
            />
            <div style={{ marginTop: 12 }}>
              <Progress
                percent={balance > 0 ? Math.min((balance / (lifetimePoints || 1)) * 100, 100) : 0}
                showInfo={false}
                strokeColor="#f1a134"
                size="small"
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {lifetimePoints} lifetime points earned
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card className="customer-card">
            <Statistic
              title="Redemption Rate"
              value={rate}
              prefix="1 pt = NPR "
              valueStyle={{ color: '#52b788', fontSize: 24 }}
            />
            {minRedemption > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Minimum {minRedemption} points to redeem
              </Text>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card
            className="customer-card"
            style={{
              textAlign: 'center',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <TrophyOutlined style={{ fontSize: 32, color: '#f1a134' }} />
            </div>
            <Button
              type="primary"
              icon={<SwapOutlined />}
              size="large"
              onClick={() => setRedeemModal(true)}
              disabled={balance < minRedemption}
              style={{
                background: 'linear-gradient(135deg, #f1a134, #e08a10)',
                border: 'none',
                borderRadius: 10,
                fontWeight: 600,
              }}
            >
              Redeem Points
            </Button>
          </Card>
        </Col>
      </Row>

      <Card
        className="customer-card"
        title="Points History"
        style={{ marginTop: 16 }}
        extra={
          <Button size="small" icon={<HistoryOutlined />} onClick={fetchData}>
            Refresh
          </Button>
        }
      >
        {history.length === 0 ? (
          <Empty description="No points history yet" />
        ) : (
          <Table
            columns={columns}
            dataSource={history}
            rowKey="id"
            pagination={{ pageSize: 10, size: 'small' }}
          />
        )}
      </Card>

      <Modal
        title="Redeem Points"
        open={redeemModal}
        onCancel={() => {
          setRedeemModal(false);
          setSelectedPoints(0);
        }}
        footer={null}
        width={420}
      >
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f1a134' }}>{balance}</div>
          <Text type="secondary">Available Points</Text>
          {redemptionOptions?.redemption_enabled === false ? (
            <Alert
              message="Redemption is currently disabled"
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
            />
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  justifyContent: 'center',
                  margin: '16px 0',
                }}
              >
                {presets
                  .filter((p) => p <= maxRedemption)
                  .map((p) => (
                    <Button
                      key={p}
                      type={selectedPoints === p ? 'primary' : 'default'}
                      onClick={() => setSelectedPoints(p)}
                      disabled={p > balance}
                      style={{
                        minWidth: 70,
                        borderRadius: 8,
                        ...(selectedPoints === p
                          ? {
                              background: 'linear-gradient(135deg, #f1a134, #e08a10)',
                              borderColor: '#e08a10',
                            }
                          : {}),
                      }}
                    >
                      {p}
                    </Button>
                  ))}
              </div>
              {selectedPoints > 0 && (
                <div
                  style={{
                    background: '#fef4e3',
                    borderRadius: 8,
                    padding: '10px 16px',
                    marginBottom: 16,
                    color: '#8a5a12',
                  }}
                >
                  You will receive <strong>{formatNPR(selectedPoints * rate)}</strong>
                </div>
              )}
              <Button
                type="primary"
                block
                size="large"
                onClick={handleRedeem}
                loading={redeeming}
                disabled={selectedPoints < minRedemption || selectedPoints > balance}
                style={{
                  background: 'linear-gradient(135deg, #f1a134, #e08a10)',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  height: 46,
                }}
              >
                <SwapOutlined /> Convert to Wallet Balance
              </Button>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CustomerRewards;
